import { Component, OnDestroy, OnInit, effect, input } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { AuthorizationService } from '@myapp/modules/fwcore/services';
import { IAuthMenu } from '../../models';
import { RequestMenu } from '../../models/messages';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


@Component({
  selector: 'app-menu-vertical',
  templateUrl: './menu-vertical.component.html',
  styleUrls: ['./menu-vertical.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet, NgFor, NgIf, NgClass, RouterLink]
})

export class MenuVerticalComponent implements OnInit, OnDestroy {

  // ---------------- Hybrid old/new input ----------------

  public impersonationEmail = input<string | null>(null);

  //----------------- data properties  ------------------

  menuItems!: IAuthMenu[];
  selected!: IAuthMenu;
  currExpandedItem!: IAuthMenu;
  baseExpandedItem!: IAuthMenu;

  //----------------- State properties  ------------------

  protected destroy$ = new Subject();

  protected prevImpersonationEmail?: string | null;

  //----------------- Constructor  ------------------
  constructor(private router: Router
    , private authService: AuthorizationService) {

    router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.refreshSelection(this.menuItems, event.url);
        }
      });

    // Listen for Okta authentication state changes
    // @ts-ignore: access to private property for demonstration; ideally expose OktaAuth via AuthorizationService
    const oktaAuth = (this.authService as any).oauth;
    if (oktaAuth && oktaAuth.authStateManager) {
      oktaAuth.authStateManager.subscribe((authState: any) => {
        if (authState.isAuthenticated) {
          this.fetchServerData();
        }
      });
    }

    effect(() => {
      const currentValue = this.impersonationEmail();
      if (currentValue !== this.prevImpersonationEmail) {
        this.prevImpersonationEmail = currentValue;
        this.refreshMenu();
      }
    });
  }

  //----------------- ng  ---------------------------

  ngOnInit(): void {
    // Do not call fetchServerData() here; wait for authentication event
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //----------------- Public methods -----------------

  retrieveData(): void {
    // Until we have a way of clearing the local storage we should not be doing this
    //const storedMenuData = localStorage.getItem('menuData');

    const storedMenuData = null;

    if (storedMenuData && JSON.parse(storedMenuData).length > 0) {
      // If data is available in local storage, use it
      const parsedData = JSON.parse(storedMenuData);
      this.updateMenuItems(parsedData);
    } else {
      // If data is not available in local storage, make the API call
      this.fetchServerData();
    }
  }

  /**
   * Refresh the menu items from the server updating the cache
   */
  public refreshMenu(): void {
    this.fetchServerData();
  }

  //----------------- Control processing   ------------------


  protected updateMenuItems(data: IAuthMenu[]): void {
    this.refreshSelection(data, this.router.url);
    this.menuItems = data;
  }

  assignLevel(item: IAuthMenu, level: number): number {

    // level is automatically generated by the templates - we keep a record here back to the item so we can implement additional features i.e. accordion at level 2
    item.level = level;

    return level;
  }

  isBookmark(item: IAuthMenu): boolean {
    if (item.url && item.url.length > 0)
      return item.url.includes("#");

    return false;
  }

  isItemRouteInternal(url: string): boolean {

    if (url.startsWith("http"))
      return false;
    else
      return true;
  }

  refreshSelection(items: IAuthMenu[], newUrl: string): boolean {
    if (!items)
      return false;

    let anySelected = false;

    for (let item of items) {

      if (item.hasChildren && item.children) {
        item.selected = this.refreshSelection(item.children, newUrl);
      }
      else {
        item.selected = !!(item.url && newUrl && newUrl.includes(item.url));
      }

      if (item.selected)
        anySelected = true;
    }

    return anySelected;
  }

  getImageClass(item: IAuthMenu): string {
    if (!item)
      return "";

    if (item.cssClass && item.cssClass.length > 0)
      return "isg-mitem-image " + item.cssClass;

    return "";
  }

  toggleSubmenu(ev: Event, item: IAuthMenu) {
    if (item) {

      var isExpanding: boolean = !(item.expanded);

      // Remove styling for the previous last expanded item
      if (this.currExpandedItem && isExpanding) {
        this.currExpandedItem.currExpanded = false;
      }

      // Create an accordion type behaviour for level 2 that only 1 item can be expanded at a time to reduce visual overload
      if (isExpanding && item.level == 2 && this.baseExpandedItem)
        this.baseExpandedItem.expanded = false;

      item.expanded = isExpanding;
      item.currExpanded = isExpanding;


      if (isExpanding) {
        // if expanding make this item the most recent expanded item
        this.currExpandedItem = item;

        // if level 2 then record so that we can implement our accordion implementation
        if (item.level == 2)
          this.baseExpandedItem = item;
      }
    }

    // do not propagate the expansion/collapse
    ev.preventDefault();
  }

  //----------------- api processing   ------------------

  protected fetchServerData(): void {
    const req: RequestMenu = new RequestMenu();
    req.hierarchyCode = 'main';

    // Ensure menu API call only happens after authentication
    this.authService.isAuthenticated().then(isAuth => {
      if (isAuth) {
        this.authService.menuGet$(req)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (data: IAuthMenu[]) => {
              this.updateMenuItems(data);
              // Store the retrieved data in local storage for future use
              localStorage.setItem('menuData', JSON.stringify(data));
            }
          );
      }
    });
  }
}
